import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import { hexToRgba, getCurrentWeek, getTotalWeeks } from './clientUtils';
import { NEUTRAL, onAccent } from './designTokens';
import ClientHero, { CLIENT_HERO_OVERLAP } from './ClientHero';

// T-13: kundens programvy. Mörkt huvud med vecka/framsteg, "Fortsätt här" med Starta pass,
// faserna som lista (klar / pågår / kommande) och övningarna i aktuell fas.

const MONTHS_SHORT = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
const fmtDate = (s) => { if (!s) return ''; const d = new Date(s + 'T12:00:00'); return `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]}`; };

function ClientProgramView({
  selectedProgram,
  selectedCp,
  blocks,
  weekSessionCounts,
  openBlock,
  onBack,
  brandAccent,
  brand,
  theme,
}) {
  const currentWeek = getCurrentWeek(selectedCp);
  const totalWeeks = getTotalWeeks(selectedCp);
  const weekProgress = totalWeeks ? Math.min(100, Math.round((currentWeek / totalWeeks) * 100)) : null;

  const sorted = blocks.slice().sort((a, b) => a.start_week - b.start_week);
  const exactBlock = sorted.find(b => currentWeek >= b.start_week && currentWeek <= b.end_week) || null;
  const nextBlock = !exactBlock ? sorted.filter(b => b.start_week > currentWeek)[0] || null : null;
  const activeBlock = exactBlock || nextBlock || null;

  const sessionsThisWeek = weekSessionCounts[selectedProgram?.id] || 0;
  const freq = selectedCp?.frequency_per_week || 0;
  const weekDone = freq > 0 && sessionsThisWeek >= freq;
  const nextWeekStart = selectedCp.start_date
    ? (() => { const d = new Date(selectedCp.start_date + 'T00:00:00'); d.setDate(d.getDate() + currentWeek * 7); return `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]}`; })()
    : null;

  // Övningar i aktuell fas (bara för visning – loggning sker i blockvyn)
  const [phaseExercises, setPhaseExercises] = useState([]);
  useEffect(() => {
    let cancelled = false;
    if (!exactBlock) { setPhaseExercises([]); return undefined; }
    (async () => {
      const { data } = await supabase
        .from('program_exercises')
        .select('id, sets, reps, duration_seconds, sort_order, exercise:exercises(title, video_url, image_url)')
        .eq('block_id', exactBlock.id)
        .order('sort_order');
      if (!cancelled) setPhaseExercises(data || []);
    })();
    return () => { cancelled = true; };
  }, [exactBlock?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const accentText = onAccent(brandAccent);
  const card = { background: theme.card_bg, border: `1px solid ${theme.card_border}`, borderRadius: '14px', boxShadow: theme.card_shadow, overflow: 'hidden' };
  const sectionHead = { padding: '12px 16px', borderBottom: `1px solid ${theme.card_border}`, fontSize: '11px', fontWeight: '600', letterSpacing: '0.08em', textTransform: 'uppercase', color: NEUTRAL.muted };
  const dose = (pe) => [pe.sets && pe.reps ? `${pe.sets} × ${pe.reps}` : pe.sets ? `${pe.sets} set` : null, pe.duration_seconds ? `${pe.duration_seconds} s` : null].filter(Boolean).join(' · ');

  return (
    <div style={{ maxWidth: '600px', margin: '0 auto', padding: '20px 16px', background: NEUTRAL.page, minHeight: '100vh' }}>
      <ClientHero
        brand={brand}
        accent={brandAccent}
        onBack={onBack}
        backLabel="← Mina program"
        label={selectedCp.start_date && currentWeek > 0 ? `Vecka ${currentWeek}${totalWeeks ? ` av ${totalWeeks}` : ''}` : 'Program'}
        title={selectedProgram.title}
        sub={[freq > 0 ? `${freq} pass i veckan` : null, selectedProgram.description || null].filter(Boolean).join(' · ') || undefined}
      >
        {weekProgress !== null && (
          <div style={{ marginTop: '14px' }}>
            <div style={{ background: 'rgba(255,255,255,0.12)', borderRadius: '999px', height: '6px' }}>
              <div style={{ width: `${weekProgress}%`, height: '6px', borderRadius: '999px', background: theme.accent_soft_bg }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: 'rgba(255,255,255,0.6)', marginTop: '6px' }}>
              <span>{fmtDate(selectedCp.start_date)}</span><span>{fmtDate(selectedCp.end_date)}</span>
            </div>
          </div>
        )}
      </ClientHero>

      <div style={{ marginTop: `-${CLIENT_HERO_OVERLAP - 8}px`, position: 'relative', display: 'flex', flexDirection: 'column', gap: '12px', padding: '0 4px' }}>

        {/* Fortsätt här / veckan klar / nästa fas */}
        {activeBlock && (
          <div
            onClick={exactBlock ? () => openBlock(exactBlock) : undefined}
            style={{ background: exactBlock ? brandAccent : theme.card_bg, color: exactBlock ? accentText : NEUTRAL.ink, border: exactBlock ? 'none' : `1px solid ${theme.card_border}`, borderRadius: '14px', padding: '16px', cursor: exactBlock ? 'pointer' : 'default', boxShadow: exactBlock ? `0 6px 18px ${hexToRgba(brandAccent, 0.3)}` : theme.card_shadow, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}
          >
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '0.08em', textTransform: 'uppercase', opacity: exactBlock ? 0.85 : 1, color: exactBlock ? undefined : NEUTRAL.muted }}>
                {!exactBlock ? 'Nästa fas' : weekDone ? 'Veckan är klar' : 'Fortsätt här'}
              </div>
              <div style={{ fontSize: '16px', fontWeight: '700', marginTop: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{activeBlock.title}</div>
              <div style={{ fontSize: '12px', opacity: exactBlock ? 0.85 : 1, color: exactBlock ? undefined : NEUTRAL.muted }}>
                {!exactBlock
                  ? `Börjar vecka ${activeBlock.start_week}`
                  : weekDone
                    ? `${sessionsThisWeek} av ${freq} pass klara${nextWeekStart ? ` · nästa vecka startar ${nextWeekStart}` : ''}`
                    : [phaseExercises.length > 0 ? `${phaseExercises.length} övningar` : null, freq > 0 ? `${sessionsThisWeek} av ${freq} pass klara` : null].filter(Boolean).join(' · ') || `Vecka ${activeBlock.start_week}–${activeBlock.end_week}`}
              </div>
            </div>
            {exactBlock && (
              <button type="button" onClick={(e) => { e.stopPropagation(); openBlock(exactBlock); }} style={{ background: accentText === '#FFFFFF' ? '#FFFFFF' : NEUTRAL.ink, color: accentText === '#FFFFFF' ? NEUTRAL.ink : '#FFFFFF', border: 'none', padding: '9px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', flexShrink: 0 }}>
                {weekDone ? 'Öppna ändå' : 'Starta pass'}
              </button>
            )}
          </div>
        )}

        {/* Faser */}
        <div style={card}>
          <div style={sectionHead}>Faser</div>
          {sorted.length === 0 ? (
            <div style={{ padding: '20px 16px', fontSize: '13px', color: NEUTRAL.muted, textAlign: 'center' }}>Inga faser i programmet ännu.</div>
          ) : sorted.map((block, i) => {
            const isPast = currentWeek > block.end_week;
            const isNow = exactBlock && exactBlock.id === block.id;
            const isFuture = currentWeek < block.start_week;
            return (
              <div
                key={block.id}
                onClick={isFuture ? undefined : () => openBlock(block)}
                style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', borderBottom: i < sorted.length - 1 ? `1px solid ${theme.card_border}` : 'none', background: isNow ? theme.accent_muted : 'transparent', opacity: isPast ? 0.6 : 1, cursor: isFuture ? 'default' : 'pointer' }}
              >
                <span style={{ width: '26px', height: '26px', borderRadius: '999px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '700', boxSizing: 'border-box',
                  background: isPast ? '#DCFCE7' : isNow ? brandAccent : 'transparent',
                  color: isPast ? '#15803D' : isNow ? accentText : NEUTRAL.faint,
                  border: isFuture ? `1px dashed ${NEUTRAL.line}` : 'none' }}>
                  {isPast ? '✓' : i + 1}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: isFuture ? NEUTRAL.muted : NEUTRAL.ink }}>Fas {i + 1} · {block.title}</div>
                  <div style={{ fontSize: '12px', color: isFuture ? NEUTRAL.faint : isNow ? NEUTRAL.text : NEUTRAL.muted }}>
                    Vecka {block.start_week}–{block.end_week}{isPast ? ' · klar' : isNow ? ' · pågår' : ''}{block.description ? ` · ${block.description}` : ''}
                  </div>
                </div>
                {!isFuture && <span aria-hidden="true" style={{ fontSize: '14px', color: NEUTRAL.line }}>›</span>}
              </div>
            );
          })}
        </div>

        {/* Övningar i aktuell fas */}
        {exactBlock && phaseExercises.length > 0 && (
          <div style={card}>
            <div style={sectionHead}>Övningar i fas {sorted.indexOf(exactBlock) + 1}</div>
            {phaseExercises.map((pe, i) => (
              <div key={pe.id} onClick={() => openBlock(exactBlock)} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 16px', borderBottom: i < phaseExercises.length - 1 ? `1px solid ${theme.card_border}` : 'none', cursor: 'pointer' }}>
                <div style={{ width: '44px', height: '32px', borderRadius: '6px', flexShrink: 0, overflow: 'hidden', background: pe.exercise?.video_url ? NEUTRAL.ink : NEUTRAL.line, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {pe.exercise?.video_url
                    ? <svg width="10" height="10" viewBox="0 0 14 14" aria-hidden="true"><path d="M4 2.5v9l7-4.5z" fill="#FFFFFF" /></svg>
                    : pe.exercise?.image_url ? <img src={pe.exercise.image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : null}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: NEUTRAL.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pe.exercise?.title || 'Övning'}</div>
                  {dose(pe) && <div style={{ fontSize: '12px', color: NEUTRAL.muted }}>{dose(pe)}</div>}
                </div>
                <span aria-hidden="true" style={{ fontSize: '14px', color: NEUTRAL.line }}>›</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default ClientProgramView;
